import type {CurrencyCode, ObjectFieldsAssertion, Values} from '@wix/devzai-utils-common';
import {arrayRemoveNullValues, currencyFormatPrice, currencyGetAllCodes, urlJoin} from '@wix/devzai-utils-common';
import type {WixProduct} from '@wix/devzai-common-wix';
import {wixProductMediaTryConvertToWixMediaResource} from '@wix/devzai-common-wix';
import type {DoppeActionPriceData} from './doppe-action-price-data';
import type {DoppeActionPrice} from './doppe-action-price';

export const DoppeDtoStoreProductType = {
    Unspecified: 'unspecified_product_type',
    Physical: 'physical',
    Digital: 'digital'
} as const;

type DoppeDtoStoreProductType = Values<typeof DoppeDtoStoreProductType>;

export interface DoppeDtoStoreProduct {
    id: string;
    slug?: string;
    name: string;
    description: string;
    descriptionPlainText: string;
    media?: WixProduct.Media;
    price: DoppeDtoStoreProduct.PriceData;
    productPageUrl?: string;
    discount?: WixProduct.Discount;
    stock?: WixProduct.Stock;
    productType: DoppeDtoStoreProductType;
    ribbon?: WixProduct['ribbon'];
    productOptions?: WixProduct.ProductOption[];
    additionalInfoSections?: WixProduct.AdditionalInfoSection[];
    customTextFields?: WixProduct.CustomTextField[];
    /**
     * Whether variants are managed for this product.
     * When true, the product has variant-level inventory and pricing.
     */
    manageVariants?: boolean;
}

export namespace DoppeDtoStoreProduct {
    export type Id = DoppeDtoStoreProduct['id'];

    export type PriceData = WixProduct.PriceData;

    export type ProductType = DoppeDtoStoreProductType;

    export type ForUpdate = Pick<DoppeDtoStoreProduct, 'id' | 'price' | 'name' | 'descriptionPlainText' | 'description' | 'media' | 'productType'>

    export type ForCreation = Pick<DoppeDtoStoreProduct, 'price' | 'name'| 'descriptionPlainText' | 'description' | 'media' | 'productType'>

    /**
     * A product variant representing a specific combination of options.
     */
    export interface Variant {
        /**
         * Variant ID.
         */
        id: string | null;
        /**
         * Mapping of option names to selected choice values.
         * Example: { "Color": "Blue", "Size": "XL" }
         */
        choices: Record<string, string>;
        /**
         * Whether this variant is in stock.
         */
        inStock: boolean;
        /**
         * Whether this variant is visible.
         */
        visible: boolean;
        /**
         * Variant-specific price data if different from the main product.
         */
        priceData?: PriceData;
        /**
         * SKU for this variant.
         */
        sku?: string;
    }
}

export function doppeStoreProductResolveUrl(productPageUrl: WixProduct.PageUrl) {
    if (productPageUrl?.base && productPageUrl?.path) {
        return urlJoin(productPageUrl.base,productPageUrl.path);
    }

    return undefined;
}

export const DoppeDtoStoreProductForCreationFieldsAssertions: ObjectFieldsAssertion<DoppeDtoStoreProduct.ForCreation> = {
    name: assert => assert.isString(),
    description: assert => assert.isString(),
    descriptionPlainText: assert => assert.isString(),
    price: assert => assert.usingAssertionFunction(() => {
        // TODO
    }),
    media: assert => assert.optional.usingAssertionFunction(() => {
        // TODO
    }),
    productType: assert => assert.usingAssertionFunction(() => {
        // TODO
    }),
}

export const DoppeDtoStoreProductForUpdateFieldsAssertions: ObjectFieldsAssertion<DoppeDtoStoreProduct.ForUpdate> = {
    id: assert => assert.isString(),
    name: assert => assert.isString(),
    description: assert => assert.isString(),
    descriptionPlainText: assert => assert.isString(),
    price: assert => assert.usingAssertionFunction(() => {
        // TODO
    }),
    media: assert => assert.optional.usingAssertionFunction(() => {
        // TODO
    }),
    productType: assert => assert.usingAssertionFunction(() => {
        // TODO
    }),
}

export function doppeDtoStoreProductPriceDataConvertToActionPriceData (price: DoppeDtoStoreProduct.PriceData) : DoppeActionPriceData {
    const actualPrice = doppeDtoStoreProductConvertToFormattedPrice(price, '');
    const discountPrice = doppeDtoStoreProductConvertToFormattedDiscountPrice(price, '');

    return {
        actualPrice: actualPrice,
        discountPrice: actualPrice !== discountPrice ? discountPrice : ''
    }
}

export function doppeDtoStoreProductPriceDataConvertToActionPrice(price: DoppeDtoStoreProduct.PriceData) : DoppeActionPrice {
    return {
        actualPrice: price.price ?? 1,
        discountPrice: price.discountedPrice,
        currencyCode: price.currency ? price.currency as CurrencyCode : 'USD'
    }
}

export function doppeDtoProductGetMedia (dtoStoreProduct: DoppeDtoStoreProduct) {
    return arrayRemoveNullValues([
        dtoStoreProduct.media?.mainMedia ? wixProductMediaTryConvertToWixMediaResource(dtoStoreProduct.media?.mainMedia) : null,
    ])
}

export function doppeDtoProductGetMediaItems (dtoStoreProduct: DoppeDtoStoreProduct) {
    return arrayRemoveNullValues(dtoStoreProduct.media?.items?.map(item => wixProductMediaTryConvertToWixMediaResource(item)) ?? [])
}

export function doppeDtoStoreProductConvertToFormattedPrice<EmptyValue>(priceData: DoppeDtoStoreProduct.PriceData, emptyValue: EmptyValue): string | EmptyValue {
    const currency = priceData.currency as CurrencyCode;

    return priceData.price && (priceData.currency && currencyGetAllCodes().includes(currency)) ? currencyFormatPrice({
        code: currency,
        price: priceData.price
    }) : (priceData.formatted?.price ?? emptyValue)
}

export function doppeDtoStoreProductConvertToFormattedDiscountPrice<EmptyValue>(priceData: DoppeDtoStoreProduct.PriceData, emptyValue: EmptyValue): string | EmptyValue {
    const currency = priceData.currency as CurrencyCode;

    return priceData.discountedPrice && (priceData.currency && currencyGetAllCodes().includes(currency)) ? currencyFormatPrice({
        code: currency,
        price: priceData.discountedPrice
    }) : (priceData.formatted?.discountedPrice ?? emptyValue)
}

/**
 * Options for creating a dummy store product with localized content and images.
 */
export interface DoppeDtoStoreProductCreateDummyOptions {
    /**
     * Localized product name.
     */
    name: string;
    /**
     * Localized product description.
     */
    description: string;
    /**
     * Product image (WixImageInfo format).
     */
    image: WixProduct.Media['mainMedia'];
    /**
     * Localized option names and choice values for variants.
     */
    options: {
        colorName: string;
        sizeName: string;
        colorBlack: string;
        colorWhite: string;
        colorBlue: string;
        sizeS: string;
        sizeM: string;
        sizeL: string;
    };
}

/**
 * Creates a dummy store product with placeholder content.
 * Used when no actual product is available (e.g., for preview or empty state).
 */
export function doppeDtoStoreProductCreateDummy(
    options: DoppeDtoStoreProductCreateDummyOptions
): DoppeDtoStoreProduct {
    const {
        name,
        description,
        image,
        options: optionLabels
    } = options;

    // Create product options for Color and Size
    const productOptions: WixProduct.ProductOption[] = [
        {
            name: optionLabels.colorName,
            optionType: 'color',
            choices: [
                { value: optionLabels.colorBlack, inStock: true, visible: true },
                { value: optionLabels.colorWhite, inStock: true, visible: true },
                { value: optionLabels.colorBlue, inStock: true, visible: true }
            ]
        },
        {
            name: optionLabels.sizeName,
            optionType: 'drop_down',
            choices: [
                { value: optionLabels.sizeS, inStock: true, visible: true },
                { value: optionLabels.sizeM, inStock: true, visible: true },
                { value: optionLabels.sizeL, inStock: true, visible: true }
            ]
        }
    ];


    return {
        id: 'dummy-product',
        slug: 'dummy-product',
        name: name,
        description: `<p>${description}</p>`,
        descriptionPlainText: description,
        media: image ? {
            mainMedia: image,
            items: [image]
        } : undefined,
        price: {
            price: 99.99,
            currency: 'USD',
            formatted: {
                price: '$99.99'
            }
        },
        productType: DoppeDtoStoreProductType.Physical,
        stock: {
            inStock: true,
            trackInventory: false
        },
        productOptions: productOptions,
        manageVariants: true
    };
}